import React, { useState, useEffect } from "react";
import { motion } from "motion/react";
import { ShoppingBag, Package, ArrowRight, Sparkles } from "lucide-react";
import { useStore } from "@/src/contexts/StoreContext";
import { ProductCard } from "./ProductCard";
import { CartDrawer } from "./CartDrawer";
import { CheckoutForm } from "./CheckoutForm";
import { OrderHistory } from "./OrderHistory";
import { cn } from "@/src/lib/utils";

interface PrintStoreProps {
  selectedImage: string | null;
  onBack: () => void;
}

export function PrintStore({ selectedImage, onBack }: PrintStoreProps) {
  const { products, cart, fetchProducts, formatPrice, getCartTotal } = useStore();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isCheckingOut, setIsCheckingOut] = useState(false);
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [addedProduct, setAddedProduct] = useState<string | null>(null);

  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  const handleProductAdded = (productName: string) => {
    setAddedProduct(productName);
    setTimeout(() => setAddedProduct(null), 2000);
  };

  const cartItemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = getCartTotal();

  // Group products by category
  const productsByCategory: Record<string, typeof products> = products.reduce((acc, product) => {
    if (!acc[product.category]) {
      acc[product.category] = [];
    }
    acc[product.category].push(product);
    return acc;
  }, {} as Record<string, typeof products>);

  const categoryNames: Record<string, string> = {
    canvas: "Canvas Prints",
    framed: "Framed Photos",
    badge: "Photo Badges",
    print: "Photo Prints",
  };

  if (isCheckingOut) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-xl mx-auto bg-white/[0.02] border border-white/5 rounded-2xl p-6"
      >
        <CheckoutForm
          onBack={() => setIsCheckingOut(false)}
          onComplete={() => {
            setIsCheckingOut(false);
            setShowOrderHistory(true);
          }}
        />
      </motion.div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-blue-400" />
            Print Store
          </h2>
          <p className="text-white/50 mt-1">
            Turn your memories into physical keepsakes
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowOrderHistory(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white/5 text-white/80 rounded-xl hover:bg-white/10 transition-all"
          >
            <Package className="w-4 h-4" />
            <span className="hidden sm:inline">Orders</span>
          </button>
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex items-center gap-2 px-4 py-2 bg-white text-black font-medium rounded-xl hover:bg-white/90 transition-all relative"
          >
            <ShoppingBag className="w-4 h-4" />
            <span className="hidden sm:inline">Cart</span>
            {cartItemCount > 0 && (
              <span className="absolute -top-2 -right-2 w-5 h-5 bg-blue-500 text-white text-xs font-bold rounded-full flex items-center justify-center">
                {cartItemCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Selected Image Preview */}
      {selectedImage && (
        <div className="bg-white/[0.02] border border-white/5 rounded-xl p-4 flex items-center gap-4">
          <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
            <img src={selectedImage} alt="Selected" className="w-full h-full object-cover" />
          </div>
          <div className="flex-1">
            <h3 className="font-medium text-white">Selected Image</h3>
            <p className="text-sm text-white/50">This image will be used for your prints</p>
          </div>
          <button
            onClick={onBack}
            className="flex items-center gap-2 px-4 py-2 text-white/60 hover:text-white transition-colors"
          >
            Change
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Products by Category */}
      {Object.entries(productsByCategory).map(([category, categoryProducts]) => (
        <div key={category} className="space-y-4">
          <div className="flex items-center gap-3">
            <h3 className="text-lg font-semibold text-white">{categoryNames[category] || category}</h3>
            <div className="flex-1 h-px bg-white/5" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {(categoryProducts as typeof products).map((product) => (
              <ProductCard
                key={product.id}
                product={product}
                imageUrl={selectedImage || "https://via.placeholder.com/400x300"}
                onAdded={() => handleProductAdded(product.name)}
              />
            ))}
          </div>
        </div>
      ))}

      {/* Empty State */}
      {products.length === 0 && (
        <div className="text-center py-12">
          <div className="w-16 h-16 border-2 border-white/10 border-t-blue-500 rounded-full animate-spin mx-auto mb-4" />
          <p className="text-white/40">Loading products...</p>
        </div>
      )}

      {/* Added Notification */}
      {addedProduct && (
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 50 }}
          className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-green-500 text-white px-6 py-3 rounded-xl shadow-lg flex items-center gap-2 z-50"
        >
          <Sparkles className="w-4 h-4" />
          {addedProduct} added to cart!
        </motion.div>
      )}

      {/* Cart Drawer */}
      <CartDrawer
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        onCheckout={() => {
          setIsCartOpen(false);
          setIsCheckingOut(true);
        }}
      />

      {/* Order History */}
      <OrderHistory isOpen={showOrderHistory} onClose={() => setShowOrderHistory(false)} />
    </div>
  );
}